import React, { useEffect, useState } from "react";

import ReactDOM from "react-dom";

import { uploadResumeToCloudinary } from "../../../utils/uploadResumeToCloudinary";

import {
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  Award,
  Code,
  FileText,
  AlertCircle,
} from "lucide-react";

import { FaWhatsapp } from "react-icons/fa6";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER;

/* ============================================================
   INPUT COMPONENT
============================================================ */

const Input = ({ icon, error, children }) => {
  return (
    <div className="space-y-1">
      <div
        className={`
          flex
          items-center
          rounded-xl
          border
          theme-card-secondary
          transition-all
          duration-300
          ${
            error
              ? "border-red-500"
              : `
                theme-border
                focus-within:border-[var(--brand-primary)]
                focus-within:ring-1
                focus-within:ring-[var(--brand-primary)]/30
              `
          }
        `}
      >
        {/* Icon */}

        <div className="flex flex-shrink-0 items-center px-3 brand-color">
          {icon}
        </div>

        {/* Input Content */}

        {children}
      </div>

      {/* Error */}

      {error && (
        <p className="flex items-center gap-1 px-1 text-xs text-red-400">
          <AlertCircle size={14} />
          {error}
        </p>
      )}
    </div>
  );
};

/* ============================================================
   WHATSAPP APPLICATION MODAL
============================================================ */

const WhatsAppApplicationModal = ({ isOpen, onClose, job }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    jobTitle: "",
    experience: "",
    skills: "",
  });

  const [errors, setErrors] = useState({});
  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);

  /* ============================================================
     LOAD JOB DETAILS
  ============================================================ */

  useEffect(() => {
    if (job) {
      setFormData((prev) => ({
        ...prev,
        jobTitle: job.title || "",
        experience: job.experience || "",
        skills: Array.isArray(job.skills)
          ? job.skills.join(", ")
          : job.skills || "",
      }));
    }
  }, [job]);

  /* ============================================================
     VALIDATION
  ============================================================ */

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Applicant name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
    }

    if (!formData.experience.trim()) {
      newErrors.experience = "Experience is required.";
    }

    if (!formData.skills.trim()) {
      newErrors.skills = "Skills are required.";
    }

    if (!resume) {
      newErrors.resume = "Resume is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ============================================================
     INPUT CHANGE
  ============================================================ */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const onlyNumbers = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        phone: onlyNumbers,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear field error

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /* ============================================================
     RESUME CHANGE
  ============================================================ */

  const handleResumeChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;

    setResume(selectedFile);

    if (errors.resume) {
      setErrors((prev) => ({
        ...prev,
        resume: "",
      }));
    }
  };

  /* ============================================================
     RESET FORM
  ============================================================ */

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      jobTitle: job?.title || "",
      experience: job?.experience || "",
      skills: Array.isArray(job?.skills)
        ? job.skills.join(", ")
        : job?.skills || "",
    });

    setErrors({});
    setResume(null);
  };

  /* ============================================================
     SUBMIT APPLICATION
  ============================================================ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setUploading(true);

      // Upload resume to Cloudinary

      const resumeData = await uploadResumeToCloudinary(resume);

      // WhatsApp message

      const message = `*New Job Application*

👤 Applicant Name: ${formData.name}

📧 Email Address: ${formData.email}

📱 Phone Number: ${formData.phone}

💼 Position Applied: ${formData.jobTitle}

⭐ Experience: ${formData.experience}

🛠 Skills: ${formData.skills}

📄 Resume: ${resumeData.url}

Please let me know the next steps in the recruitment process. I look forward to hearing from you.`;

      const encodedMessage = encodeURIComponent(message);

      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

      window.open(whatsappUrl, "_blank", "noopener,noreferrer");

      resetForm();

      setTimeout(() => {
        onClose();
      }, 300);
    } catch (error) {
      console.error("Resume Upload Error:", error);

      alert("Failed to upload resume. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  /* ============================================================
     CLOSE MODAL WHEN NOT OPEN
  ============================================================ */

  if (!isOpen) {
    return null;
  }

  /* ============================================================
     MODAL
  ============================================================ */

  const modal = (
    <div
      className="
        fixed
        inset-0
        z-[999]
        flex
        items-center
        justify-center
        bg-black/70
        backdrop-blur-md
        p-3
        sm:p-5
      "
    >
      {/* ======================================================
          MODAL CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          flex
          w-full
          max-w-md
          flex-col
          overflow-hidden
          rounded-2xl
          sm:max-w-lg
          lg:max-w-xl
          lg:rounded-3xl
          max-h-[92vh]
          border
          theme-border
          theme-card
          shadow-[0_20px_70px_var(--shadow-color)]
          transition-colors
          duration-300
        "
      >
        {/* ====================================================
            HEADER
        ==================================================== */}

        <div
          className="
            sticky
            top-0
            z-20
            flex
            items-start
            justify-between
            gap-4
            border-b
            theme-border
            theme-card
            px-5
            py-4
            sm:px-6
            sm:py-5
          "
        >
          {/* Header Content */}

          <div className="min-w-0">
            <h2
              className="
                text-xl
                font-bold
                sm:text-2xl
                theme-text-primary
              "
            >
              Apply via WhatsApp
            </h2>

            <p
              className="
                mt-1
                text-xs
                leading-relaxed
                sm:text-sm
                theme-text-muted
              "
            >
              Fill in your details and continue on WhatsApp.
            </p>
          </div>

          {/* Close Button */}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="
              flex
              h-10
              w-10
              flex-shrink-0
              items-center
              justify-center
              rounded-full
              theme-bg-tertiary
              theme-border
              transition-all
              duration-300
              hover:bg-[var(--bg-secondary)]
              hover:scale-105
            "
          >
            <X
              size={18}
              className="theme-text-primary"
            />
          </button>
        </div>

        {/* ====================================================
            FORM
        ==================================================== */}

        <form
          onSubmit={handleSubmit}
          className="
            space-y-4
            overflow-y-auto
            custom-scrollbar
            px-5
            py-5
            sm:px-6
            sm:py-6
          "
        >
          {/* ==================================================
              APPLICANT NAME
          ================================================== */}

          <Input
            icon={<User size={18} />}
            error={errors.name}
          >
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Applicant Name"
              className="
                w-full
                bg-transparent
                py-3
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
                outline-none
                placeholder:text-[var(--text-muted)]
              "
            />
          </Input>

          {/* ==================================================
              EMAIL
          ================================================== */}

          <Input
            icon={<Mail size={18} />}
            error={errors.email}
          >
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email Address"
              className="
                w-full
                bg-transparent
                py-3
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
                outline-none
                placeholder:text-[var(--text-muted)]
              "
            />
          </Input>

          {/* ==================================================
              PHONE
          ================================================== */}

          <Input
            icon={<Phone size={18} />}
            error={errors.phone}
          >
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              maxLength={10}
              placeholder="Phone Number"
              className="
                w-full
                bg-transparent
                py-3
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
                outline-none
                placeholder:text-[var(--text-muted)]
              "
            />
          </Input>

          {/* ==================================================
              JOB TITLE
          ================================================== */}

          <Input
            icon={<Briefcase size={18} />}
          >
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              readOnly
              className="
                w-full
                cursor-not-allowed
                bg-transparent
                py-3
                pr-4
                text-sm
                font-medium
                sm:text-base
                theme-text-primary
                outline-none
              "
            />
          </Input>

          {/* ==================================================
              EXPERIENCE
          ================================================== */}

          <Input
            icon={<Award size={18} />}
            error={errors.experience}
          >
            <input
              type="text"
              name="experience"
              value={formData.experience}
              readOnly
              className="
                w-full
                cursor-not-allowed
                bg-transparent
                py-3
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
                outline-none
              "
            />
          </Input>

          {/* ==================================================
              SKILLS
          ================================================== */}

          <Input
            icon={<Code size={18} />}
            error={errors.skills}
          >
            <div
              className="
                flex
                min-h-[52px]
                w-full
                items-center
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
              "
            >
              {formData.skills}
            </div>
          </Input>

          {/* ==================================================
              RESUME
          ================================================== */}

          <Input
            icon={<FileText size={18} />}
            error={errors.resume}
          >
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeChange}
              className="
                w-full
                cursor-pointer
                bg-transparent
                py-3
                pr-4
                text-sm
                sm:text-base
                theme-text-primary
                outline-none

                file:mr-3
                file:cursor-pointer
                file:rounded-md
                file:border-0
                file:bg-[var(--brand-primary)]
                file:px-3
                file:py-2
                file:font-medium
                file:text-white
                file:transition-all
                file:duration-300

                hover:file:bg-[var(--brand-hover)]
              "
            />
          </Input>

          {/* ==================================================
              SUBMIT BUTTON
          ================================================== */}

          <button
            type="submit"
            disabled={uploading}
            className="
              mt-2
              flex
              w-full
              items-center
              justify-center
              gap-2
              rounded-xl
              brand-bg
              py-3
              sm:py-3.5
              text-sm
              font-semibold
              text-white
              sm:text-base
              transition-all
              duration-300
              hover:bg-[var(--brand-hover)]
              hover:shadow-[0_8px_25px_color-mix(in_srgb,var(--brand-primary)_25%,transparent)]
              hover:-translate-y-[1px]
              disabled:cursor-not-allowed
              disabled:opacity-60
            "
          >
            {uploading ? (
              <>
                <div
                  className="
                    h-5
                    w-5
                    animate-spin
                    rounded-full
                    border-2
                    border-white
                    border-t-transparent
                  "
                />

                Uploading...
              </>
            ) : (
              <>
                <FaWhatsapp size={18} />

                Continue to WhatsApp
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );

  /* ============================================================
     PORTAL
  ============================================================ */

  return ReactDOM.createPortal(modal, document.body);
};

export default WhatsAppApplicationModal;